import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

function Stars({ n, size = "w-4 h-4", onSelect = null }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= n ? "fill-[#FF007F] text-[#FF007F]" : "text-black/30"} ${onSelect ? "cursor-pointer" : ""}`}
          onClick={onSelect ? () => onSelect(i) : undefined}
          data-testid={onSelect ? `star-${i}` : undefined}
        />
      ))}
    </div>
  );
}

export default function ProductReviews({ productId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], avg: 0, count: 0 });
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/products/${productId}/reviews`);
      setData(res);
    } catch (err) {
      console.warn("Failed to load reviews", err);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const myReview = useMemo(
    () => (user ? data.reviews.find((r) => r.user_id === user.user_id) : null),
    [user, data.reviews]
  );

  useEffect(() => {
    if (myReview) {
      setRating(myReview.rating);
      setTitle(myReview.title || "");
      setComment(myReview.comment || "");
    }
  }, [myReview]);

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) { toast.error("Write a review first"); return; }
    setLoading(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, title, comment });
      toast.success(myReview ? "Review updated" : "Thanks for the review");
      if (!myReview) { setTitle(""); setComment(""); setRating(5); }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setLoading(false); }
  };

  const del = async (rid) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/reviews/${rid}`);
    toast.success("Deleted");
    load();
  };

  return (
    <section className="mt-16 border-t-4 border-black pt-10" data-testid="reviews-section">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="sticker pink">REVIEWS</span>
          <h2 className="font-display text-4xl md:text-5xl mt-2">WHAT THE STREET SAYS</h2>
        </div>
        {data.count > 0 && (
          <div className="text-right">
            <Stars n={Math.round(data.avg)} size="w-6 h-6" />
            <p className="font-mono-b text-sm mt-1"><b>{data.avg}</b> / 5 · {data.count} review{data.count !== 1 ? "s" : ""}</p>
          </div>
        )}
      </div>

      {/* Add / edit review form */}
      {user ? (
        <form onSubmit={submit} className="mt-6 bg-white border-2 border-black brutal-shadow p-5" data-testid="review-form">
          <p className="font-mono-b text-xs uppercase font-bold mb-2">{myReview ? "UPDATE YOUR REVIEW" : "DROP A REVIEW"}</p>
          <div className="mb-3">
            <p className="text-xs font-mono-b font-bold uppercase mb-1">RATING</p>
            <Stars n={rating} size="w-7 h-7" onSelect={setRating} />
          </div>
          <input
            data-testid="review-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Headline (optional)"
            className="brutal-input w-full mb-3"
          />
          <textarea
            data-testid="review-comment"
            required
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Fit, fabric, vibe — say it real."
            className="brutal-input w-full"
          />
          <button
            data-testid="review-submit"
            disabled={loading}
            type="submit"
            className="mt-3 bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-5 py-2 font-mono-b font-bold uppercase disabled:opacity-50"
          >
            {loading ? "SAVING..." : myReview ? "UPDATE" : "POST REVIEW"}
          </button>
        </form>
      ) : (
        <div className="mt-6 border-2 border-dashed border-black p-6 font-mono-b text-sm" data-testid="review-login-prompt">
          <a href="/login" className="underline font-bold">Log in</a> to drop a review.
        </div>
      )}

      {/* Reviews list */}
      <div className="mt-6 space-y-3">
        {data.reviews.length === 0 ? (
          <p className="font-mono-b text-sm text-black/60 border-2 border-dashed border-black p-6 text-center" data-testid="reviews-empty">No reviews yet. Be the first.</p>
        ) : data.reviews.map((r) => (
          <div key={r.review_id} data-testid={`review-${r.review_id}`} className="bg-white border-2 border-black brutal-shadow-sm p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <Stars n={r.rating} />
                  {r.title && <span className="font-display text-xl">{r.title}</span>}
                </div>
                <p className="font-mono-b text-xs text-black/60 mt-1">by <b>{r.user_name}</b> · {new Date(r.created_at).toLocaleDateString()}</p>
              </div>
              {user && (r.user_id === user.user_id || user.role === "admin") && (
                <button data-testid={`review-delete-${r.review_id}`} onClick={() => del(r.review_id)} className="text-[#FF007F]"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
            <p className="font-mono-b text-sm mt-2 whitespace-pre-line">{r.comment}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
